import { useMemo, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router";
import { CalendarPlus, ExternalLink, MoveRight, UserX, Undo2, X } from "lucide-react";
import { ApiError } from "../../lib/api";
import { usePeople } from "../../lib/admin";
import {
  BOOKING_SOURCES,
  LOW_SCORE,
  NEXT_STEP_LABEL,
  PRIORITY_LABEL,
  SOURCE_LABEL,
  dateShort,
  dayLabel,
  timeLabel,
  useBooking,
  useBookings,
  useResponses,
  whenShort,
  type BookingDetail as Detail,
  type BookingJson,
  type BookingView,
} from "../../lib/bookings";
import { StatTile } from "../../components/stats";
import {
  BackLink,
  Badge,
  Button,
  Callout,
  Card,
  CardHeader,
  CardList,
  CardRow,
  CardSection,
  Drawing,
  Empty,
  Fact,
  FactList,
  PageHeader,
  SearchInput,
  SelectField,
  Skeleton,
  StatGrid,
  TabBar,
  Table,
  TableEmptyRow,
  TableScroll,
  TableSkeletonRows,
} from "../../components/ui";
import { usePageTitle } from "./components";
import { BookError, BookGate, BookSomeoneIn, BookingStateBadge, CancelBooking, MoveBooking, SourceText, useNoShow, useOwnerTz } from "./book-components";

/** The booking lists, and the survey answers (`responses`), as tabs over one panel. */
type View = BookingView | "responses";

const VIEWS: { value: View; label: string }[] = [
  { value: "upcoming", label: "Upcoming" },
  { value: "past", label: "Past" },
  { value: "cancelled", label: "Cancelled" },
  { value: "no_show", label: "No-shows" },
  { value: "responses", label: "Responses" },
];

const EMPTY: Record<BookingView, string> = {
  upcoming: "No upcoming calls.",
  past: "No past calls yet.",
  cancelled: "No cancelled calls.",
  no_show: "No no-shows.",
};

export function AdminBookings() {
  usePageTitle("Bookings");
  const [modal, setModal] = useState(false);
  const navigate = useNavigate();
  return (
    <>
      <PageHeader
        title="Bookings"
        subtitle="Calls booked on book.metrickle.com, and the ones you booked in. Each one is on the person's timeline in People."
        actions={
          <Button variant="primary" onClick={() => setModal(true)}>
            <CalendarPlus size={14} aria-hidden /> Book someone in
          </Button>
        }
      />
      <BookGate>
        <BookingList />
      </BookGate>
      <BookSomeoneIn open={modal} onOpenChange={setModal} onBooked={(b) => navigate(`/admin/bookings/${b.id}`)} />
    </>
  );
}

function BookingList() {
  const [params, setParams] = useSearchParams();
  const view = (VIEWS.some((v) => v.value === params.get("view")) ? params.get("view") : "upcoming") as View;
  const source = BOOKING_SOURCES.includes(params.get("source") as never) ? params.get("source")! : "all";
  const offset = Math.max(0, Number(params.get("from")) || 0);
  const [search, setSearch] = useState(params.get("q") ?? "");
  const tz = useOwnerTz();
  const responses = view === "responses";
  const q = useBookings({ view: responses ? "upcoming" : view, q: params.get("q") ?? "", source, offset });
  const update = (patch: Record<string, string | null>) =>
    setParams(
      (p) => {
        const next = new URLSearchParams(p);
        for (const [k, v] of Object.entries(patch)) {
          if (v === null || v === "" || (k === "view" && v === "upcoming") || (k === "source" && v === "all")) next.delete(k);
          else next.set(k, v);
        }
        if (!("from" in patch)) next.delete("from");
        return next;
      },
      { replace: true },
    );
  const rows = q.data?.bookings ?? [];
  const filtered = !!(params.get("q") || source !== "all");

  return (
    <Card>
      <div className="flex flex-wrap items-center justify-between gap-3 px-4 pt-3 pb-3">
        <TabBar aria-label="Which calls" panelId="bookings-panel" items={VIEWS} value={view} onChange={(v) => update({ view: v })} />
        <div className={responses ? "hidden" : "flex flex-wrap items-center gap-2"}>
          <form
            role="search"
            onSubmit={(e) => {
              e.preventDefault();
              update({ q: search.trim() });
            }}
          >
            <SearchInput label="Search bookings by name or email" wrapperClassName="w-full @xl:w-64" value={search} onChange={(e) => setSearch(e.target.value)} onBlur={() => search.trim() !== (params.get("q") ?? "") && update({ q: search.trim() })} placeholder="Name or email" />
          </form>
          <SelectField
            aria-label="Source"
            className="w-44"
            items={[{ value: "all", label: "Any source" }, ...BOOKING_SOURCES.map((s) => ({ value: s, label: SOURCE_LABEL[s] }))]}
            value={source}
            onChange={(v) => update({ source: v })}
          />
        </div>
      </div>
      <div id="bookings-panel" role="tabpanel" aria-label={VIEWS.find((v) => v.value === view)!.label}>
        {responses ? (
          <ResponsesPanel offset={offset} onPage={(from) => update({ from: String(from) })} />
        ) : q.isError ? (
          <div className="border-t border-line">
            <BookError error={q.error} title="Couldn't load bookings" />
          </div>
        ) : (
          <TableScroll label="Bookings" divided>
            <Table caption={`${VIEWS.find((v) => v.value === view)!.label} calls, ${view === "upcoming" ? "soonest" : "latest"} first, in your time (${tz.replace(/_/g, " ")}). Select a name to open the booking.`} busy={q.isFetching} className="min-w-[760px]">
              <thead>
                <tr>
                  <th scope="col">Person</th>
                  <th scope="col">When</th>
                  <th scope="col">Call</th>
                  <th scope="col">Source</th>
                  <th scope="col">Status</th>
                </tr>
              </thead>
              <tbody>
                {q.isPending && <TableSkeletonRows rows={5} columns={5} />}
                {q.data && !rows.length && <TableEmptyRow columns={5}>{filtered ? "No calls match these filters." : EMPTY[view as BookingView]}</TableEmptyRow>}
                {rows.map((b) => (
                  <tr key={b.id}>
                    <th scope="row" className="font-normal">
                      <Link to={`/admin/bookings/${b.id}`} className="font-medium text-ink hover:underline">
                        {b.detailsDeleted ? "Details deleted" : (b.name ?? b.email)}
                      </Link>
                      {!b.detailsDeleted && <div className="truncate text-xs text-ink-3">{b.email}</div>}
                    </th>
                    <td className="text-[13px] text-ink">{whenShort(b.start, b.end, tz)}</td>
                    <td className="text-[13px] text-ink-2">{b.eventType.title}</td>
                    <td className="text-[13px] text-ink-2">
                      <SourceText source={b.source} createdBy={b.createdBy} note={b.createdBy === "invitee" ? b.ref : null} />
                    </td>
                    <td>
                      <BookingStateBadge booking={b} />
                      {b.survey && <div className="mt-0.5 text-xs text-ink-2">Rated {b.survey.usefulness} of 5</div>}
                      {askDidTheyShow(b) && <div className="mt-0.5 text-xs text-ink-2">Did they join? Survey at {timeLabel(b.followup!.dueAt!, tz)}</div>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </TableScroll>
        )}
        {!responses && (offset > 0 || q.data?.nextOffset != null) && (
          <CardSection className="flex justify-between gap-2">
            <Button size="sm" variant="ghost" disabled={offset === 0} onClick={() => update({ from: String(Math.max(0, offset - 50)) })}>
              Previous 50
            </Button>
            <Button size="sm" variant="ghost" disabled={q.data?.nextOffset == null} onClick={() => update({ from: String(q.data!.nextOffset) })}>
              Next 50
            </Button>
          </CardSection>
        )}
      </div>
    </Card>
  );
}

// --- Responses: the survey after each call ---------------------------------------------------------

/** What they chose next, with a low score called out in words (never colour alone). */
function ScoreText({ usefulness }: { usefulness: number }) {
  return (
    <span className="inline-flex flex-wrap items-center gap-1.5">
      <span className="tabular">{usefulness} of 5</span>
      {usefulness <= LOW_SCORE && <Badge tone="bad">Low score</Badge>}
    </span>
  );
}

function ResponsesPanel({ offset, onPage }: { offset: number; onPage: (from: number) => void }) {
  const q = useResponses(offset);
  const tz = useOwnerTz();
  if (q.isError)
    return (
      <div className="border-t border-line">
        <BookError error={q.error} title="Couldn't load responses" />
      </div>
    );
  const t = q.data?.totals;
  const rows = q.data?.responses ?? [];
  return (
    <>
      <div className="border-t border-line">
        <StatGrid loading={q.isPending} cols={3} count={6}>
          {t && (
            <>
              <StatTile label="Responses" value={t.responses} note={t.surveysSent ? `From ${t.surveysSent} surveys sent` : undefined} />
              <StatTile label="Average usefulness" value={t.averageUsefulness === null ? "None yet" : `${t.averageUsefulness.toFixed(1)} of 5`} />
              <StatTile label="Low scores" value={t.lowScores} hint={`Rated ${LOW_SCORE} or less out of 5. You were emailed about each one.`} />
              <StatTile label="Want a proposal" value={t.proposal} />
              <StatTile label="Want a trial" value={t.trial} />
              <StatTile label="Not yet" value={t.notYet} hint="Tagged nurture. Nothing more is sent to them." />
            </>
          )}
        </StatGrid>
        {q.data && (
          <p className="border-t border-line px-4 py-2 text-xs text-ink-2">
            {q.data.lastReceivedAt ? `Last answer received ${dateShort(q.data.lastReceivedAt, tz)}, ${timeLabel(q.data.lastReceivedAt, tz)}.` : "No answers received yet."} The survey goes 90 minutes after each call.
          </p>
        )}
      </div>
      <TableScroll label="Survey responses" divided>
        <Table caption={`Survey answers, newest first, in your time (${tz.replace(/_/g, " ")}). Select a name to open the booking.`} busy={q.isFetching} className="min-w-[760px]">
          <thead>
            <tr>
              <th scope="col">Person</th>
              <th scope="col">Call</th>
              <th scope="col">How useful</th>
              <th scope="col">What matters most</th>
              <th scope="col">Next step</th>
            </tr>
          </thead>
          <tbody>
            {q.isPending && <TableSkeletonRows rows={3} columns={5} />}
            {q.data && !rows.length && <TableEmptyRow columns={5}>No answers yet. They appear here as people answer the survey after their call.</TableEmptyRow>}
            {rows.map((r) => (
              <tr key={r.bookingId}>
                <th scope="row" className="font-normal">
                  <Link to={`/admin/bookings/${r.bookingId}`} className="font-medium text-ink hover:underline">
                    {r.detailsDeleted ? "Details deleted" : (r.name ?? r.email)}
                  </Link>
                  <div className="text-xs text-ink-3">
                    Answered {dateShort(r.receivedAt, tz)}
                    {r.hasNote && " · left a note"}
                  </div>
                </th>
                <td className="text-[13px] text-ink-2">
                  {r.eventType.title}
                  <div className="text-xs text-ink-3">{whenShort(r.start, r.end, tz)}</div>
                </td>
                <td className="text-[13px] text-ink">
                  <ScoreText usefulness={r.usefulness} />
                </td>
                <td className="text-[13px] text-ink-2">{PRIORITY_LABEL[r.priority]}</td>
                <td className="text-[13px] text-ink">{NEXT_STEP_LABEL[r.nextStep]}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      </TableScroll>
      {(offset > 0 || q.data?.nextOffset != null) && (
        <CardSection className="flex justify-between gap-2">
          <Button size="sm" variant="ghost" disabled={offset === 0} onClick={() => onPage(Math.max(0, offset - 50))}>
            Previous 50
          </Button>
          <Button size="sm" variant="ghost" disabled={q.data?.nextOffset == null} onClick={() => onPage(q.data!.nextOffset!)}>
            Next 50
          </Button>
        </CardSection>
      )}
    </>
  );
}

// --- One booking ---------------------------------------------------------------------------------

export function AdminBooking() {
  const { bookingId } = useParams();
  const q = useBooking(bookingId!);
  const b = q.data?.booking;
  usePageTitle(b ? (b.detailsDeleted ? "Booking" : (b.name ?? b.email ?? "Booking")) : "Booking");
  return (
    <>
      <BackLink to="/admin/bookings" className="mb-3">
        All bookings
      </BackLink>
      {q.isError ? (
        q.error instanceof ApiError && q.error.status === 404 ? (
          <Empty title="Booking not found" illustration={<Drawing name="notFound" />} />
        ) : (
          <Card>
            <BookError error={q.error} title="Couldn't load this booking" />
          </Card>
        )
      ) : !q.data ? (
        <Skeleton className="h-96" />
      ) : (
        <BookingDetail detail={q.data} />
      )}
    </>
  );
}

function BookingDetail({ detail }: { detail: NonNullable<ReturnType<typeof useBooking>["data"]> }) {
  const b = detail.booking;
  const tz = useOwnerTz();
  const [modal, setModal] = useState<"move" | "cancel" | null>(null);
  const noShow = useNoShow(b);
  const people = usePeople();
  const person = useMemo(() => (b.email ? people.data?.find((p) => p.email === b.email!.toLowerCase()) : undefined), [people.data, b.email]);
  const theirs = b.timezone !== tz;
  return (
    <>
      <PageHeader
        title={
          <span className="flex flex-wrap items-center gap-2">
            {b.detailsDeleted ? "Details deleted" : (b.name ?? b.email)} <BookingStateBadge booking={b} />
          </span>
        }
        subtitle={`${b.eventType.title} · ${dayLabel(b.start, tz)}, ${timeLabel(b.start, tz)} to ${timeLabel(b.end, tz)}`}
      />
      <div className="-mt-2 mb-5 flex flex-wrap items-center gap-2">
        {person && (
          <Link to={`/admin/people/${person.id}`} className="inline-flex items-center gap-1 text-sm text-accent-text hover:underline">
            Open in People <MoveRight size={13} aria-hidden />
          </Link>
        )}
        {b.status === "confirmed" && b.started && (
          <Button onClick={noShow.toggle} pending={noShow.busy} pendingLabel="Saving…">
            {b.noShow ? (
              <>
                <Undo2 size={14} aria-hidden /> Clear no-show
              </>
            ) : (
              <>
                <UserX size={14} aria-hidden /> Mark as no-show
              </>
            )}
          </Button>
        )}
        {b.canChange && (
          <>
            <Button onClick={() => setModal("move")}>Move</Button>
            <Button onClick={() => setModal("cancel")}>
              <X size={14} aria-hidden /> Cancel call
            </Button>
          </>
        )}
      </div>
      {askDidTheyShow(b) && (
        <div className="mb-4">
          <Callout tone="neutral" title={`Did ${b.name ?? "they"} join?`}>
            If they did, there's nothing to do: the survey goes at {timeLabel(b.followup!.dueAt!, tz)}. If they didn't, choose Mark as no-show above before then, and they'll get “Sorry we missed each other” with a link to book again instead.
          </Callout>
        </div>
      )}
      <div className="grid gap-4 @4xl:grid-cols-[1fr_360px]">
        <div className="min-w-0 space-y-4">
          <Card>
            <CardHeader title="Booking" />
            <FactList cols={2} className="px-4 pb-4">
              <Fact term="When (your time)">{whenShort(b.start, b.end, tz)}</Fact>
              <Fact term="Their time">{theirs ? `${whenShort(b.start, b.end, b.timezone)} (${b.timezone.replace(/_/g, " ")})` : "Same as yours"}</Fact>
              <Fact term="Email">{b.email ? <a href={`mailto:${b.email}`} className="text-accent-text hover:underline">{b.email}</a> : <span className="text-ink-3">Deleted</span>}</Fact>
              <Fact term="Google Meet">
                {b.meetUrl ? (
                  <a href={b.meetUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-accent-text hover:underline">
                    Join the call <ExternalLink size={12} aria-hidden />
                    <span className="sr-only">(opens in a new tab)</span>
                  </a>
                ) : (
                  <span className="text-ink-3">{b.status === "cancelled" ? "None" : "Not made yet"}</span>
                )}
              </Fact>
              <Fact term="How it came about">
                {b.createdBy === "invitee" ? "They booked on the booking page" : `You booked them in · ${SOURCE_LABEL[b.source]}`}
                {b.sourceNote && <span className="block text-xs text-ink-3">{b.sourceNote}</span>}
                {b.ref && <span className="block text-xs text-ink-3">From the link tagged {b.ref}</span>}
              </Fact>
              <Fact term="Booked">{dateShort(b.createdAt, tz)}</Fact>
              {b.status === "cancelled" && (
                <Fact term="Cancelled">
                  {b.cancelledBy === "owner" ? "By you" : "By them"}
                  {b.cancelReason && <span className="block text-xs text-ink-3">{b.cancelReason}</span>}
                </Fact>
              )}
              {b.emailSuppressed && <Fact term="Emails">Their address bounced or complained, so no more emails go to it.</Fact>}
              {b.followup && <Fact term="After the call">{followupText(b, tz)}</Fact>}
            </FactList>
          </Card>
          <SurveyCard detail={detail} tz={tz} />
          <Card>
            <CardHeader title="Their answers" subtitle="From the booking form" />
            <CardList empty={b.detailsDeleted ? "Deleted with their details." : "No answers."}>
              {detail.answers.map((a) => (
                <CardRow key={a.key} wrap>
                  <span className="text-sm text-ink-2">{a.label}</span>
                  <span className="text-sm text-ink">{Array.isArray(a.value) ? a.value.join(", ") : a.value}</span>
                </CardRow>
              ))}
            </CardList>
          </Card>
        </div>
        <Card>
          <CardHeader title="History" />
          <CardList empty="Nothing yet.">
            {detail.history.map((h, i) => (
              <CardRow key={`${h.at}-${i}`} wrap>
                <span className="text-sm text-ink">
                  {HISTORY[h.action] ?? h.action} <span className="text-ink-3">· {h.actor === "owner" ? "you" : h.actor === "invitee" ? "them" : "automatic"}</span>
                </span>
                <span className="text-xs text-ink-3">{dateShort(h.at, tz)}, {timeLabel(h.at, tz)}</span>
              </CardRow>
            ))}
          </CardList>
        </Card>
      </div>
      <MoveBooking booking={b} open={modal === "move"} onOpenChange={(o) => setModal(o ? "move" : null)} />
      <CancelBooking booking={b} open={modal === "cancel"} onOpenChange={(o) => setModal(o ? "cancel" : null)} />
    </>
  );
}

/** A call that has ended, isn't marked, and whose survey hasn't gone yet: worth asking whether they joined. */
const askDidTheyShow = (b: BookingJson) => b.status === "confirmed" && !b.noShow && Date.parse(b.end) <= Date.now() && b.followup?.state === "pending" && b.followup.kind === "survey" && !!b.followup.dueAt;

/** Where the email after the call stands, in words. */
function followupText(b: BookingJson, tz: string): string {
  const f = b.followup!;
  const at = (iso: string) => `${dateShort(iso, tz)}, ${timeLabel(iso, tz)}`;
  const what = f.kind === "no_show" ? "“Sorry we missed each other”" : "The survey";
  if (f.state === "pending") return f.dueAt ? `${what} goes ${at(f.dueAt)}` : `${what} goes 90 minutes after the call`;
  if (f.state === "sent") return `${what} went ${f.sentAt ? at(f.sentAt) : ""}`.trim();
  if (f.state === "off") return "No survey: it's off for this call type";
  if (f.state === "cancelled") return "Nothing: the call was cancelled";
  return "Nothing sent: their details are gone or their address bounced";
}

function SurveyCard({ detail, tz }: { detail: Detail; tz: string }) {
  const s = detail.survey;
  const b = detail.booking;
  if (!s) {
    const f = b.followup;
    const empty =
      f?.state === "sent" && f.kind === "survey"
        ? "No answer yet."
        : f?.state === "sent"
          ? "They were sent “Sorry we missed each other” instead of the survey."
          : f?.state === "pending" && f.kind === "survey" && f.dueAt
            ? `The survey goes ${dateShort(f.dueAt, tz)}, ${timeLabel(f.dueAt, tz)}.`
            : "No survey for this call.";
    return (
      <Card>
        <CardHeader title="Survey" subtitle="Their answers after the call" />
        <CardList empty={empty}>{null}</CardList>
      </Card>
    );
  }
  const r = s.routing;
  const happened = [
    r?.lowScore || r?.proposal ? "You were emailed at once." : null,
    r?.proposal ? "They were emailed about their proposal (with the scoping-call link, if you have a scoping call type), and a “Send a proposal” task is on their timeline in People." : null,
    r?.trial ? "They were sent the link to sign up to Metrickle." : null,
    r?.nurture ? "Tagged nurture. Nothing more is sent to them." : null,
  ].filter((x): x is string => !!x);
  return (
    <Card>
      <CardHeader title="Survey" subtitle={`Answered ${dateShort(s.receivedAt, tz)}, ${timeLabel(s.receivedAt, tz)}`} />
      <FactList cols={2} className="px-4 pb-4">
        <Fact term="How useful">{s.usefulness === null ? "Not answered" : <ScoreText usefulness={s.usefulness} />}</Fact>
        <Fact term="What matters most">{s.priority ? PRIORITY_LABEL[s.priority] : "Not answered"}</Fact>
        <Fact term="Next step">{s.nextStep ? NEXT_STEP_LABEL[s.nextStep] : "Not answered"}</Fact>
        <Fact term="Their note">{s.note ? <span className="whitespace-pre-line">{s.note}</span> : <span className="text-ink-3">{b.detailsDeleted ? "Deleted with their details" : "None"}</span>}</Fact>
        {happened.length > 0 && (
          <Fact term="What happened" className="@md:col-span-2">
            <ul className="list-disc space-y-0.5 pl-4">
              {happened.map((h) => (
                <li key={h}>{h}</li>
              ))}
            </ul>
          </Fact>
        )}
      </FactList>
    </Card>
  );
}

const HISTORY: Record<string, string> = {
  "survey.answered": "Answered the survey",
  "booking.created": "Booked",
  "booking.moved": "Moved",
  "booking.cancelled": "Cancelled",
  "booking.details_deleted": "Details deleted",
  "booking.no_show": "Marked as a no-show",
  "booking.no_show_cleared": "No-show cleared",
};
